import { ChevronDown, ChevronUp, Globe } from 'lucide-react'
import { motion } from 'motion/react'
import { Collapse } from '@/components/Collapse'
import { Button } from '@/components/ui/button'
import { attentionFlash, staggerItem } from '@/lib/motion/variants'
import { categoryLabel, severityLabel } from '@/lib/siteHealth'
import {
  explanationParagraphs,
  findingRowId,
  pluralize,
} from './SiteHealthFormat'
import {
  CARD_CLASS,
  EYEBROW_CLASS,
  severityBadgeClass,
} from './SiteHealthStyles'
import type { InspectorFindingRowProps } from './siteHealthInspector.types'

// The flash sits above the card and takes no clicks, so it never blocks the header.
const ATTENTION_FLASH_CLASS =
  'pointer-events-none absolute inset-0 z-10 rounded-2xl ring-2 ring-inset ring-primary'

// The card clips to its own radius, so the header fill stays square at every corner.
const ROW_BUTTON_CLASS =
  'flex h-auto w-full items-start gap-3 whitespace-normal rounded-none p-4 text-left hover:bg-muted/40'

// A page-scoped finding describes the whole document, so it never earns a pin.
const WHOLE_PAGE_TAG_CLASS =
  'inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground'

function targetSummary(finding: InspectorFindingRowProps['finding']): string {
  if (finding.pageScoped) return 'Applies to the whole page'

  if (finding.targets.length === 0) return 'No marked elements on this page'

  if (finding.targetTotal > finding.targets.length) {
    return `${pluralize(finding.targets.length, 'marked element')} of ${finding.targetTotal}`
  }

  return pluralize(finding.targets.length, 'marked element')
}

export default function InspectorFindingRow({
  finding,
  expanded,
  attentionNonce,
  onSelect,
}: InspectorFindingRowProps) {
  const paragraphs = explanationParagraphs(finding.explanation)
  const steps = finding.fixSteps ?? []
  const firstTarget = finding.targets[0]

  function select() {
    onSelect(finding.id, firstTarget?.position ?? 0)
  }

  return (
    <motion.li
      id={findingRowId(finding.id)}
      variants={staggerItem}
      className={`${CARD_CLASS} relative overflow-hidden`}
    >
      {attentionNonce !== null && (
        <motion.span
          key={attentionNonce}
          aria-hidden
          data-testid="attention-flash"
          variants={attentionFlash}
          initial="hidden"
          animate="shown"
          className={ATTENTION_FLASH_CLASS}
        />
      )}
      <Button
        type="button"
        variant="ghost"
        aria-expanded={expanded}
        onClick={select}
        className={ROW_BUTTON_CLASS}
      >
        {expanded ? (
          <ChevronUp
            className="mt-1 size-4 shrink-0 text-primary"
            aria-hidden
          />
        ) : (
          <ChevronDown
            className="mt-1 size-4 shrink-0 text-muted-foreground"
            aria-hidden
          />
        )}
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className={severityBadgeClass(finding.severity)}>
              {severityLabel(finding.severity)}
            </span>
            {finding.pageScoped && (
              <span className={WHOLE_PAGE_TAG_CLASS}>
                <Globe className="size-3.5" aria-hidden />
                Whole page
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              {categoryLabel(finding.category)}
            </span>
          </span>
          <span className="mt-2 block font-medium text-foreground">
            {finding.title}
          </span>
          <span className="mt-1 block text-xs text-muted-foreground">
            {targetSummary(finding)}
          </span>
        </span>
      </Button>

      <Collapse open={expanded}>
        <div className="space-y-5 border-t border-border bg-muted/10 p-4">
          {paragraphs.length > 0 && (
            <section>
              <h4 className={EYEBROW_CLASS}>Why it matters</h4>
              <div className="mt-2 space-y-3 text-sm leading-6 text-foreground">
                {paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </section>
          )}

          <section>
            <h4 className={EYEBROW_CLASS}>How to fix</h4>
            {steps.length > 0 ? (
              <ol className="mt-3 space-y-3">
                {steps.map((step, index) => (
                  <li
                    key={`${index + 1}-${step.title}`}
                    className="grid grid-cols-[auto_1fr] gap-3"
                  >
                    <span className="flex size-6 items-center justify-center rounded-full bg-muted text-xs tabular-nums text-muted-foreground ring-1 ring-border">
                      {index + 1}
                    </span>
                    <div>
                      <p className="font-medium leading-6">{step.title}</p>
                      <p className="text-sm leading-6 text-muted-foreground">
                        {step.description}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="mt-2 text-sm text-muted-foreground">
                Structured fix steps are not available for this finding type.
              </p>
            )}
          </section>

          {finding.targets.length > 0 && (
            <section>
              <h4 className={EYEBROW_CLASS}>Marked elements</h4>
              <ul className="mt-3 space-y-2">
                {finding.targets.map((target) => (
                  <li key={target.position}>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => onSelect(finding.id, target.position)}
                      className="h-auto w-full justify-start gap-2 whitespace-normal px-2 py-1.5 text-left"
                    >
                      <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-xs tabular-nums text-primary-foreground">
                        {target.position}
                      </span>
                      <span className="min-w-0 flex-1 text-sm text-muted-foreground">
                        {target.note ?? target.selector}
                      </span>
                    </Button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </Collapse>
    </motion.li>
  )
}
